import { Button } from '@/components/form/baseInputs/Button';
import { SlashCircle01Icon } from '@/components/icons/SlashCircle01Icon';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';

import { useDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import { isBeneficiaryDesignationEntityType } from '../../dispositiveProvisions.label';
import { useDispositiveProvisionsTemplateSplitScreenModalContext } from '../../DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal.context';
import { useOrderedDyingClients } from '../hooks/utilityHooks';

interface DispositiveProvisionsRecipientsEmptyStateProps {
  onAddRecipients: () => void;
}

export function DispositiveProvisionsRecipientsEmptyState({
  onAddRecipients,
}: DispositiveProvisionsRecipientsEmptyStateProps) {
  const canEdit = useCanEditDispositiveProvisions();
  const { isClientProfile, entityType } = useDispositiveProvisionsContext();
  const isBenDesignation = isBeneficiaryDesignationEntityType(entityType);
  const { isTemplateMode } =
    useDispositiveProvisionsTemplateSplitScreenModalContext();
  const [firstClientToDie, secondClientToDie] = useOrderedDyingClients();
  return (
    <ActionCard
      variant="noCard"
      heading={
        isBenDesignation
          ? 'No beneficiary designations upon death'
          : 'No dispositions upon death'
      }
      description={getNoneDispositionDescription({
        firstClientToDie,
        secondClientToDie,
        isClientProfile,
        isTemplateMode,
        isBenDesignation,
      })}
      icon={<SlashCircle01Icon size={40} sx={{ color: COLORS.GRAY[200] }} />}
      actions={
        <Button
          variant="secondary"
          size="sm"
          fullWidth
          onClick={onAddRecipients}
          disabled={!canEdit}
        >
          Add a distribution
        </Button>
      }
    />
  );
}

interface GetNoneDispositionDescriptionOpts {
  isClientProfile: boolean;
  firstClientToDie: ContextPrimaryClient;
  secondClientToDie?: ContextPrimaryClient;
  isTemplateMode: boolean;
  isBenDesignation: boolean;
}

function getNoneDispositionDescription({
  firstClientToDie,
  secondClientToDie,
  isClientProfile,
  isTemplateMode,
  isBenDesignation,
}: GetNoneDispositionDescriptionOpts): string {
  const label = isBenDesignation ? 'beneficiary designations' : 'dispositions';

  if (isTemplateMode) {
    return `No ${label} have been set`;
  }

  const prefix = (() => {
    if (isClientProfile) {
      return `There are no ${label}`;
    }

    return 'This entity will remain as-is';
  })();

  if (!secondClientToDie) {
    return `${prefix} when ${firstClientToDie.displayGivenName} passes`;
  }

  return `${prefix} if ${firstClientToDie.displayGivenName} dies first and ${secondClientToDie.displayGivenName} subsequently dies`;
}
